/**
 * attachmentNaming.js
 *
 * Filename helpers shared by NewCaseWizard (pre-submit pending files) and
 * CaseDetail (post-submit uploads via the slot UI).
 *
 * Every uploaded file is renamed to `AE{caseId}-{Category}.{ext}` on upload,
 * with `-2`, `-3` suffixes when multiple files exist for the same category.
 * Original filenames are discarded so every clinic uploads the same canonical
 * names, and the slot UI can bucket existing files back to their category by
 * parsing the name.
 */

import { ATTACHMENT_CATEGORY_LABELS, ATTACHMENT_CATEGORIES } from './eventVocabularies.js';

export function pascalise(text) {
  return (text || '')
    .trim()
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('') || 'Attachment';
}

export function fileExt(file) {
  const name = typeof file === 'string' ? file : file?.name || '';
  const dot  = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot) : '';
}

export function categoryToken(item) {
  if (item.category === 'other') return pascalise(item.otherLabel || 'Other');
  const label = ATTACHMENT_CATEGORY_LABELS[item.category] || item.category;
  return pascalise(label);
}

export function previewFileName(caseRef, item, sameNameCount = 0) {
  const suffix = sameNameCount > 0 ? `-${sameNameCount + 1}` : '';
  return `${caseRef}-${categoryToken(item)}${suffix}${fileExt(item.file)}`;
}

export function buildUploadFile(item, caseId, sameNameCount = 0) {
  const suffix  = sameNameCount > 0 ? `-${sameNameCount + 1}` : '';
  const newName = `AE${caseId}-${categoryToken(item)}${suffix}${fileExt(item.file)}`;
  return new File([item.file], newName, { type: item.file.type });
}

// ── Reverse mapping ──────────────────────────────────────────────────────────
//
// Given an uploaded filename like `AE123-TrainingCertificate-2.png`, recover
// the category key and dupe index. Returns null when the name doesn't follow
// our canonical format (e.g. legacy SP filenames, files uploaded outside the
// slot UX). Falls back to category 'other' with the token as otherLabel.

const TOKEN_TO_CATEGORY_KEY = (() => {
  const map = {};
  for (const cat of ATTACHMENT_CATEGORIES) {
    const token = pascalise(cat.label);
    map[token.toLowerCase()] = cat.key;
  }
  // Legacy tokens from renamed labels. The 'photo' category was labelled
  // "Photo" until July 2026, so existing files are named AE{id}-Photo[-N];
  // they must keep bucketing into the Photos slot.
  map['photo'] = 'photo';
  return map;
})();

export function parseAttachmentName(filename) {
  if (!filename) return null;
  // AE{caseId}-{Token}[-N].{ext}
  const m = /^AE(\d+)-([A-Za-z0-9]+?)(?:-(\d+))?(\.[^.]+)?$/.exec(filename);
  if (!m) return null;
  const [, caseId, token, dupe, ext] = m;
  const categoryKey = TOKEN_TO_CATEGORY_KEY[token.toLowerCase()] || null;
  return {
    caseId,
    token,
    dupeIndex: dupe ? parseInt(dupe, 10) - 1 : 0,
    ext: ext || '',
    categoryKey,   // null when the token isn't a recognised category
  };
}

/**
 * Build a synthetic `item` shape from a category key + File, suitable for
 * previewFileName / buildUploadFile / categoryToken.
 */
export function makeItem(file, categoryKey, otherLabel = null) {
  return { file, category: categoryKey, otherLabel };
}

/**
 * Rename a file dropped into the comment composer to follow the same
 * `AE{caseId}-{Token}[-N].{ext}` convention used everywhere else. Comments
 * don't carry a category, so the token is derived from the original filename
 * (pascalised). Dupes are counted against the supplied existingNames list.
 */
export function buildCommentUploadFile(file, caseId, existingNames = []) {
  const baseName = (file.name || '').replace(/\.[^.]+$/, '');
  const token = pascalise(baseName);
  const ext   = fileExt(file);
  let dupes = 0;
  for (const name of existingNames) {
    const parsed = parseAttachmentName(name);
    if (parsed?.token === token) dupes++;
  }
  const suffix = dupes > 0 ? `-${dupes + 1}` : '';
  return new File([file], `AE${caseId}-${token}${suffix}${ext}`, { type: file.type });
}
